import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import {
  MatDialogRef,
  MAT_DIALOG_DATA,
  MatDialogTitle,
  MatDialogContent,
  MatDialogActions
} from '@angular/material/dialog';
import { Observable, Observer, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { Invitation } from '../../../dto/invitation';
import { InvitationRequest } from '../../../dto/invitation-request';
import { ValidateInvitations } from '../../../dto/validate-invitations';
import { MfaService } from '../../../services/mfa.service';
import { CdkScrollable } from '@angular/cdk/scrolling';
import { FlexModule } from '@angular/flex-layout/flex';
import { NgFor, NgIf } from '@angular/common';
import {
  MatFormField,
  MatSuffix,
  MatLabel
} from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { FormsModule } from '@angular/forms';
import { MatTooltip } from '@angular/material/tooltip';
import { MatIcon } from '@angular/material/icon';
import { MatButton } from '@angular/material/button';
import { ExtendedModule } from '@angular/flex-layout/extended';
import { MatCheckbox } from '@angular/material/checkbox';

@Component({
  selector: 'mfa-add-users',
  templateUrl: './add-users.component.html',
  styleUrls: ['./add-users.component.scss'],
  imports: [
    MatDialogTitle,
    CdkScrollable,
    MatDialogContent,
    FlexModule,
    NgFor,
    MatFormField,
    MatLabel,
    MatInput,
    FormsModule,
    MatTooltip,
    MatIcon,
    MatSuffix,
    NgIf,
    MatButton,
    ExtendedModule,
    MatDialogActions,
    MatCheckbox
  ]
})
export class AddUsersComponent implements OnInit, OnDestroy {
  constructor(
    @Inject(MAT_DIALOG_DATA) public data: InvitationRequest,
    private dialogRef: MatDialogRef<AddUsersComponent>,
    private mfa: MfaService
  ) {}

  ngOnInit(): void {}
  onDestroy$ = new Subject<void>();
  validating = false;
  hasError = false;
  isAddClicked = false;
  takenUsernames: string[] | null = null;
  trackByIndex(index: number) {
    return index;
  }

  ngOnDestroy() {
    this.onDestroy$.next();
  }
  addUser() {
    this.data.invitations.push(new Invitation('', '', []));
  }

  import(file: File | null | undefined) {
    if (!file) {
      return;
    }
    this.parse(file)
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((inv) => this.data.invitations.push(...inv));
  }

  parse(csvFile: File): Observable<Array<Invitation>> {
    const csvParserObserver = new Observable(
      (observer: Observer<Array<Invitation>>) => {
        try {
          const reader = new FileReader();
          reader.readAsText(csvFile);

          reader.onload = () => {
            const csvData = reader.result;
            const csvRecordsArray = this.csvStringToArray(
              (csvData as string).trim()
            );
            observer.next(csvRecordsArray);
            observer.complete();
          };

          reader.onerror = () => {
            observer.error('Unable to parse CSV File.');
          };
        } catch {
          observer.error('Unknown error parsing csv.');
        }
      }
    );

    return csvParserObserver;
  }

  csvStringToArray(csvDataString: string) {
    const regexPattern = new RegExp(
      '(\\,|\\r?\\n|\\r|^)(?:"((?:\\\\.|""|[^\\\\"])*)"|([^\\,"\\r\\n]*))',
      'gi'
    );
    let matchedPatternArray = regexPattern.exec(csvDataString);
    let horizontalIndex = 0;
    const resultCSV = new Array<Invitation>(new Invitation('', '', []));
    while (matchedPatternArray) {
      if (matchedPatternArray[1].length && matchedPatternArray[1] !== ',') {
        resultCSV.push(new Invitation('', '', []));
        horizontalIndex = 0;
      }
      const cleanValue = matchedPatternArray[2]
        ? matchedPatternArray[2].replace(new RegExp('[\\\\"](.)', 'g'), '$1')
        : matchedPatternArray[3];
      const currentInvitation = resultCSV[resultCSV.length - 1];
      if (horizontalIndex === 0) {
        currentInvitation.username = cleanValue;
      } else if (horizontalIndex === 1) {
        currentInvitation.email = cleanValue;
      } else {
        currentInvitation.additionalUsernames.push(cleanValue);
      }
      horizontalIndex++;
      matchedPatternArray = regexPattern.exec(csvDataString);
    }
    return resultCSV;
  }

  isUserNameExists(index: number): boolean {
    const usersNames = this.data.invitations.map((i) =>
      i.username.includes('\\')
        ? i.username.toLowerCase()
        : '.\\' + i.username.toLowerCase()
    );

    const currentUser = this.data.invitations[index].username.includes('\\')
      ? this.data.invitations[index].username.toLowerCase()
      : '.\\' + this.data.invitations[index].username.toLowerCase();

    if (
      usersNames.filter((u) => u === currentUser && currentUser !== '.\\')
        .length > 1
    ) {
      return true;
    }

    if (this.data.invitations[index].userAlreadyExists) {
      return true;
    }
    return false;
  }

  isEmailExists(index: number): boolean {
    const emails = this.data.invitations.map((i) => i.email.toLowerCase());
    const currrentEmail = this.data.invitations[index].email.toLowerCase();
    if (emails.filter((e) => e === currrentEmail).length > 1) {
      return true;
    }

    if (this.data.invitations[index].emailExists) {
      return true;
    }
    return false;
  }

  aditionalUsersExists(index: number): string {
    const users = this.data.invitations
      .map((i) => [i.username, ...i.additionalUsernames])
      .reduce((acc, val) => acc.concat(val), [])
      .map((username) =>
        username.includes('\\')
          ? username.toLowerCase()
          : '.\\' + username.toLowerCase()
      );

    const currentAdditionalUsers = this.data.invitations[
      index
    ].additionalUsernames.map((user) =>
      user.includes('\\') ? user.toLowerCase() : '.\\' + user.toLowerCase()
    );

    for (const user of currentAdditionalUsers) {
      if (users.filter((u) => u === user && user !== '.\\').length > 1) {
        return user;
      }
    }

    if (this.takenUsernames && this.takenUsernames.length > 0) {
      for (const user of currentAdditionalUsers) {
        if (this.takenUsernames.includes(user)) {
          return user;
        }
      }
    }

    return '';
  }

  add() {
    if (!this.data.customerId) {
      return;
    }

    this.isAddClicked = true;
    this.validating = true;
    this.hasError = false;
    this.refreshAlreadyExists();
    this.normalizeData();
    this.checkData();

    this.mfa
      .validateInvitations(
        this.data.customerId,
        this.data.resourceId,
        new ValidateInvitations(
          this.data.invitations
            .map((i) => [i.username, ...i.additionalUsernames])
            .reduce((acc, val) => acc.concat(val), []),
          this.data.invitations.map((i) => i.email)
        )
      )
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((validate) => {
        this.validating = false;
        if (
          (validate?.usernames?.length ?? 0) > 0 ||
          (validate?.emails?.length ?? 0) > 0
        ) {
          this.takenUsernames = validate.usernames.map((username) =>
            username.toLowerCase()
          );

          this.data.invitations.forEach((i) => {
            const name = i.username.includes('\\')
              ? i.username.toLowerCase()
              : '.\\' + i.username.toLowerCase();
            i.userAlreadyExists = this.takenUsernames
              ? this.takenUsernames.includes(name)
              : false;
            i.emailExists = validate.emails
              .map((email) => email.toLowerCase())
              .includes(i.email.toLowerCase());
          });
        } else if (!this.hasError) {
          this.isAddClicked = false;
          this.dialogRef.close(this.data);
        }
      });
  }

  checkData() {
    this.validating = false;
    for (const [index, invitation] of this.data.invitations.entries()) {
      for (const [i, a] of invitation.additionalUsernames.entries()) {
        if (a.trim() === '' || a.trim() === '.\\') {
          this.data.invitations[index].additionalUsernames.splice(i, 1);
        }
      }
      if (
        !invitation.username ||
        invitation.username.trim() === '' ||
        !invitation.email ||
        invitation.email.trim() === '' ||
        !this.isValidEmail(invitation.email)
      ) {
        this.hasError = true;
      }
      if (
        this.isUserNameExists(index) ||
        this.isEmailExists(index) ||
        this.aditionalUsersExists(index) !== ''
      ) {
        this.hasError = true;
      }
    }
  }

  isValidEmail(email: string): boolean {
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    return emailRegex.test(email);
  }

  refreshAlreadyExists() {
    this.data.invitations.forEach((i) => {
      i.userAlreadyExists = false;
      i.emailExists = false;
    });
  }

  normalizeData() {
    for (const [index, invitation] of this.data.invitations.entries()) {
      this.data.invitations[index].username = invitation.username.trim();
      this.data.invitations[index].email = invitation.email.trim();
      for (const [i, a] of invitation.additionalUsernames.entries()) {
        this.data.invitations[index].additionalUsernames[i] = a.trim();
      }
    }
  }
}
